import { Button, Card, Col, DatePicker, Modal, Row, Space, Table, theme, Upload } from 'antd'
import React from 'react'
import { hexToRGBA } from '../../lib/utils'
import CustomRow from '../../reusable-components/CustomRow'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons'
import dayjs from 'dayjs'
import { InboxOutlined } from '@ant-design/icons'
import usePatientPlanOverview from './hooks/usePatientPlanOverview'
import { DocumentViewer } from '../../reusable-components/DocumentViewer'
import ModalHeader from '../../reusable-components/ModalHeader'

const PatientPlanOverview = ({ currentPatient, setCurrentPatient, handleRefresh, template, planType, planLabel }) => {
  const { token } = theme.useToken()
  const {
    handleSaveVisit,
    handleFileUpload,
    addingVisit,
    setAddingVisit,
    newVisitDate,
    setNewVisitDate,
    uploadedFiles,
    pushingNewVisit,
    setUploadedFiles,
    viewingPDF,
    setViewingPDF,
    // These would be dynamic based on the plan type
    getDataComplianceColumns,
    getVisitComplianceColumns
  } = usePatientPlanOverview({
    currentPatient,
    handleRefresh,
    planType
  })

  // Generate columns based on plan type
  const dataComplianceColumns = getDataComplianceColumns(token)
  const visitComplianceColumns = getVisitComplianceColumns(token)

  return (
    <CustomRow>
      <Col span={24}>
        <FontAwesomeIcon icon={faExclamationCircle} color={token.colorWarning} /> Please note that compliance may take
        up to 24 hours to reflect from the time of submission
      </Col>
      <Col span={24}>
        <Card
          headStyle={{ background: hexToRGBA(token.colorPrimary, 0.2) }}
          size='small'
          title={`${planLabel || 'Plan'} Compliance - Tests`}
        >
          <Table
            size='small'
            bordered
            columns={dataComplianceColumns}
            dataSource={currentPatient.individualTestCompliances}
            expandable={{
              expandedRowRender: (record) => {
                return (
                  <Row style={{ background: 'white', padding: 8 }}>
                    <Col span={12}>
                      <div>
                        <Space direction='vertical' style={{ width: '100%' }}>
                          <Space>
                            <p style={{ width: 110 }}>
                              <b>Test Type:</b>
                            </p>
                            <p>{record.label}</p>
                          </Space>
                          <Space>
                            <p style={{ width: 110 }}>
                              <b>Compliance:</b>
                            </p>
                            <p>
                              <span style={{ color: record.isCompliant ? token.colorSuccess : token.colorError }}>
                                {record.isCompliant ? 'Compliant' : 'Non-Compliant'}
                              </span>
                            </p>
                          </Space>
                          <Space>
                            <p style={{ width: 110 }}>
                              <b>Tests Done:</b>
                            </p>
                            <p>
                              {record.testsCompleted} of {record.requiredTests} required
                            </p>
                          </Space>
                          <Space>
                            <p style={{ width: 110 }}>
                              <b>Outstanding:</b>
                            </p>
                            <p>{record.outstandingTests}</p>
                          </Space>
                        </Space>
                      </div>
                    </Col>
                    <Col span={12}>
                      <div>
                        <Space direction='vertical' style={{ width: '100%' }}>
                          <Space style={{ alignItems: 'baseline' }}>
                            <p style={{ width: 110 }}>
                              <b>Test Dates:</b>
                            </p>
                            <Space direction='vertical'>
                              {record.testDates.map((date, i) => (
                                <p key={i}>{dayjs(date).format('DD/MM/YYYY')}</p>
                              ))}
                            </Space>
                          </Space>
                        </Space>
                      </div>
                    </Col>
                  </Row>
                )
              }
            }}
          />
        </Card>
      </Col>
      <Col span={24}>
        <Card
          style={{ marginTop: 12 }}
          headStyle={{ background: hexToRGBA(token.colorPrimary, 0.2) }}
          size='small'
          title={`${planLabel || 'Plan'} Compliance - Visits`}
          extra={
            <Button
              type='primary'
              onClick={() => {
                setAddingVisit(true)
              }}
            >
              Add Visit
            </Button>
          }
        >
          <Table
            size='small'
            pagination={false}
            bordered
            columns={visitComplianceColumns}
            dataSource={currentPatient.individualVisitCompliances}
          />
        </Card>
      </Col>

      {planType === 'treatment' && (
        <>
          <Col span={24}>
            <Card
              style={{ marginTop: 12 }}
              headStyle={{ background: hexToRGBA(token.colorPrimary, 0.2) }}
              size='small'
              title={`${planLabel || 'Plan'} - Medications`}
            >
              <Table
                size='small'
                pagination={false}
                bordered
                columns={[
                  {
                    title: 'Medication',
                    dataIndex: 'name',
                    key: 'name'
                  },
                  {
                    title: 'Dosage',
                    dataIndex: 'dosage',
                    key: 'dosage'
                  },
                  {
                    title: 'Frequency',
                    dataIndex: 'frequency',
                    key: 'frequency',
                    render: (frequency) => {
                      // Map frequency codes to readable text
                      const frequencyMap = {
                        once_daily: 'Once daily',
                        twice_daily: 'Twice daily',
                        thrice_daily: 'Three times daily',
                        four_times_daily: 'Four times daily',
                        alternate_days: 'Every other day',
                        weekly: 'Weekly',
                        as_needed: 'As needed'
                      }
                      return frequencyMap[frequency] || frequency
                    }
                  },
                  {
                    title: 'Duration (Days)',
                    dataIndex: 'durationDays',
                    key: 'durationDays'
                  }
                ]}
                dataSource={template?.medications || []}
              />
            </Card>
          </Col>

          <Col span={24}>
            <Card
              style={{ marginTop: 12 }}
              headStyle={{ background: hexToRGBA(token.colorPrimary, 0.2) }}
              size='small'
              title={`${planLabel || 'Plan'} - Diagnostic Tests`}
            >
              <Table
                size='small'
                pagination={false}
                bordered
                columns={[
                  {
                    title: 'Test Name',
                    dataIndex: 'testName',
                    key: 'testName'
                  },
                  {
                    title: 'Type',
                    dataIndex: 'testType',
                    key: 'testType'
                  },
                  {
                    title: 'Scheduled Date',
                    dataIndex: 'scheduledDate',
                    key: 'scheduledDate',
                    render: (date) => (date ? dayjs(date).format('YYYY-MM-DD') : 'Not scheduled')
                  },
                  {
                    title: 'Status',
                    dataIndex: 'completed',
                    key: 'completed',
                    render: (completed) => (
                      <span style={{ color: completed ? token.colorSuccess : token.colorWarning }}>
                        {completed ? 'Completed' : 'Pending'}
                      </span>
                    )
                  }
                ]}
                dataSource={template?.diagnosticTests || []}
              />
            </Card>
          </Col>
        </>
      )}

      <Modal
        title={<ModalHeader title='ADD VISIT' onClose={() => setAddingVisit(false)} />}
        footer={null}
        closable={false}
        open={addingVisit}
      >
        <CustomRow>
          <Col span={24}>
            <DatePicker value={newVisitDate} onChange={(d) => setNewVisitDate(d)} />
          </Col>
          <Col span={24}>
            <Upload.Dragger
              multiple
              fileList={uploadedFiles}
              onChange={(info) => {
                setUploadedFiles(info.fileList)
              }}
              beforeUpload={() => false}
            >
              <p className='ant-upload-drag-icon'>
                <InboxOutlined />
              </p>
              <p className='ant-upload-text'>Click or drag file to this area to upload</p>
              <p className='ant-upload-hint'>
                Support for a single or bulk upload. Strictly prohibited from uploading company data or other banned
                files.
              </p>
            </Upload.Dragger>
          </Col>
          <Col span={24} style={{ marginTop: 12 }}>
            <Space>
              <Button
                loading={pushingNewVisit}
                type='primary'
                onClick={() => {
                  handleSaveVisit()
                }}
              >
                Submit
              </Button>
              <Button
                onClick={() => {
                  setAddingVisit(false)
                }}
              >
                Cancel
              </Button>
            </Space>
          </Col>
        </CustomRow>
      </Modal>
      <Modal
        width={1920}
        open={!!viewingPDF}
        closable={false}
        footer={null}
        maskClosable={false}
        title={
          <ModalHeader
            title='VIEW DOCUMENT'
            onClose={() => {
              setViewingPDF(null)
            }}
          />
        }
      >
        <DocumentViewer documentContent={viewingPDF} />
      </Modal>
    </CustomRow>
  )
}

export default PatientPlanOverview
